import { jwtDecode } from "jwt-decode";


export class OptionsValidator<T extends Record<string, any>> {
  private options: T;

  constructor(
    options: Partial<T>,
    defaults: Partial<T>,
    requiredKeys: Record<string, any>
  ) {
    
    this.options = this.deepMerge(defaults, options);

    const missingParams = this.validateRequiredParams(
      this.options,
      requiredKeys
    );

    if (missingParams.length > 0) {
      throw new Error(
        `Missing required parameters: ${missingParams.join(", ")}`
      );
    }
  }

  private deepMerge(
    defaults: Partial<T>,
    options: Partial<T>
  ): T {
    const result: Record<string, any> = { ...defaults };
    
    for (const key in options) {
      
      if (
        options[key] &&
        defaults &&
        typeof options[key] === "object" &&
        !Array.isArray(options[key])
      ) {
        
        result[key] = this.deepMerge(
          defaults[key] as Partial<T>,
          options[key] as Partial<T>
        );
      } else {
        
        result[key] = options[key];
      }
    }

    return result as T;
  }

  private validateRequiredParams(
    options: Record<string, any>,
    requiredKeys: Record<string, any>,
    path: string[] = []
  ): string[] {
    const missingParams: string[] = [];

    for (const key in requiredKeys) {
      const fullPath = [...path, key].join(".");
      const optionValue = options[key];
      const requiredValue = requiredKeys[key];

      if (
        requiredValue &&
        typeof requiredValue === "object" &&
        !Array.isArray(requiredValue)
      ) {
        if (typeof optionValue !== "object" || optionValue === null) {
          missingParams.push(fullPath);
        } else {
          missingParams.push(
            ...this.validateRequiredParams(
              optionValue as Record<string, any>,
              requiredValue,
              [...path, key]
            )
          );
        }
      } else if (requiredValue === true && optionValue === undefined) {
        missingParams.push(fullPath);
      }
    }

    return missingParams;
  }

  getOptions(): T {
    return this.options;
  }
}

export class DataUtil {
  static loadScript(url: string, async:boolean = false): Promise<string> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = async;
      script.src = url;

      script.onload = () => resolve(url);
      script.onerror = () => {
        // clean up the failed node
        document.head.removeChild(script);
        reject(new Error(`Failed to load GTM script: ${script.src}`));
      };

      document.head.appendChild(script);
     
    });

  }

  static loadCSS(href) {
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.type = "text/css";
      link.onload = () => resolve(href);
      link.onerror = () => reject(new Error(`Could not load CSS at ${href}`));
      document.head.appendChild(link);
    });
  }
}

export class DomainUtil {

  static getCurrentUrl(): URL {
    return new URL(window.location.href);
  }

  static getRootDomain(givenHostname = false): string {
    const { hostname } = this.getCurrentUrl();
    let hostnameToCheck = hostname;
    if (givenHostname && typeof givenHostname === 'string') {
      hostnameToCheck = givenHostname;
    }
    const parts = hostnameToCheck.split('.');
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }

  static getUrlParamFromScriptTag(fileName: string, param: string): string | null {
    const scriptTag = document.querySelector(`script[src*="${fileName}"]`);
    if (!scriptTag) {
      return null;
    }
    const scriptUrl = new URL(scriptTag['src']);
    return scriptUrl.searchParams.get(param);
  }

  static getBaseSiteUrl(): string {
    return DomainUtil.getCurrentUrl().host.replace(/^(?:(?:www|preprod|develop|staging)\.)+/, '');
  }

  static isTribune(): boolean {
    const rootDomain = this.getRootDomain();
    const tribList = ['mcall.com', 'courant.com', 'sun-sentinel.com', 'orlandosentinel.com', 'growthspotter.com', 'dailypress.com', 'pilotonline.com', 'nydailynews.com', 'baltimoresun.com', 'capitalgazette.com', 'chicagotribune.com', 'trbdevcloud.com'];
	  return tribList.includes( rootDomain );
  }
}

export const {
  getCurrentUrl,
  getBaseSiteUrl,
  getRootDomain,
  getUrlParamFromScriptTag,
  isTribune,
} = DomainUtil;

export class EventUtil {
  // Generic method to fire a custom event
  static fireEvent<T>(eventName: string, detail: T): void {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,    // Allow the event to bubble up the DOM tree
      cancelable: true, // Allow the event to be cancelled
    });

    document.dispatchEvent(event);
  }

  // Method to listen for a custom event
  static listenForEvent<T>(eventName: string, callback: (detail: T) => void): () => void {
    const eventListener = (event: CustomEvent) => {
      callback(event.detail);
    };

    document.addEventListener(eventName, eventListener);

    // Return a function to remove the listener if needed
    return () => {
      
      document.removeEventListener(eventName, eventListener);
    };
  }

  /**
   * 
   * @param fn This is the function to debounce any events that might be fired back to back or too quickly.
   * @param wait The number of milliseconds to wait before calling the function again.
   * @returns 
   */
  static debounce(fn, wait) {
    let lastCalled = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCalled >= wait) {
        lastCalled = now;
        fn.apply(this, args);
      }
    };
  }

}

export class JwtUtil {
  static decodeToken(jwt: string): any {
    return jwtDecode(jwt);
  }
}

export class StorageUtil {
  /**
   * Removes a key from localStorage if it exists.
   */
  static deleteStorageObject(objName: string): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      if (window.localStorage.getItem(objName) !== null) {
        window.localStorage.removeItem(objName);
      }
    } catch {
      // Swallow: localStorage access can throw in some browsers/contexts.
      return;
    }
  }
}

export class DateUtil {

  /**
   * Takes a passed in timestamp and returns true if it is today.
   * @param timestamp The timestamp to check if it is today
   * @returns True if the timestamp is today
   */
  static isTimeStampToday(timestamp: number): boolean {
    const date = new Date(timestamp);

    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    return date >= startOfDay && date < endOfDay;
  }

  /**
   * Takes a passed in timestamp and returns true if it is expired.
   * @param timestamp The timestamp to check if it is expired
   * @returns True if it is expired.
   */
  static isExpired(timestamp: number, useSeconds: boolean = true): boolean {
    //TODO: Need to handle when timestamp is 10 chars or 13 (depending if milliseconds are inlcuded)
    const now = (useSeconds) ? Math.floor(Date.now() / 1000) : Date.now();
    return timestamp < now;
  }

  static timestampToDate(timestamp: number): string {
    //if we have only 10 digits, we need to multiply by 1000 to get milliseconds
    const fixed = timestamp.toString().length === 10 ? timestamp *= 1000 : timestamp;
    return new Date(fixed).toLocaleString();
    
  }

}

export class UrlUtil{

  static hasQueryParam(param: string): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has(param);
  }

  static hasQueryParamValue(param: string, value: string): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param) === value;
  }

  static getQueryParam(param: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  static setQueryParam(param: string, value: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set(param, value);
    window.history.replaceState({}, '', url.toString());
  }

  static removeQueryParam(param: string): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState({}, '', url.toString());
  }
}

export class LazyValue<T> {
  private value?: T;

  get(): T | undefined {
    return this.value;
  }

  ensure(value: T): T {
    if (this.value === undefined) {
      this.value = value;
    }
    return this.value;
  }
}